'use client';

import { Carousel, type CarouselApi, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@shared/components/shadcn';
import { imagePathResolver } from '@shared/utils';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { CarouselDots } from '../../Carousel/components/CarouselDots/CarouselDots';
import { Link } from '../../Link/Link';
import { cn } from '../../shadcn';
import type { MagazineArticle } from '../helpers/magazineCategory';
import { MagazinePromotedArticleContent } from './MagazinePromotedArticleContent';

type MagazinePromotedCarouselProps = {
  articles: readonly MagazineArticle[];
  baseImgUrl: string;
  className?: string;
  'data-testid'?: string;
};

function MagazinePromotedCarousel({ articles, baseImgUrl, className, 'data-testid': dataTestId = 'magazine-promoted-carousel' }: MagazinePromotedCarouselProps) {
  const t = useTranslations('magazine.promoted_carousel');
  const [api, setApi] = useState<CarouselApi>();
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (!api) {
      return;
    }

    const onSelect = () => {
      setSelectedIndex(api.selectedScrollSnap());
    };

    onSelect();
    api.on('select', onSelect);

    return () => {
      api.off('select', onSelect);
    };
  }, [api]);

  if (articles.length === 0) {
    return null;
  }

  const activeArticle = articles[selectedIndex] ?? articles[0]!;

  return (
    <section className={cn('flex flex-col gap-8', className)} data-testid={dataTestId}>
      <div
        className="flex flex-col gap-4"
        data-testid={`${dataTestId}-article`}
        data-track-container="Promoted Article"
        data-track-title={activeArticle.title}
        data-track-provider={activeArticle.author.name}
        {...(activeArticle.category?.title && { 'data-track-type': activeArticle.category.title })}
      >
        <Carousel opts={{ loop: true }} setApi={setApi} fullWidth className="relative w-full">
          <div className="relative">
            <CarouselContent className="-ml-0">
              {articles.map((article, index) => (
                <CarouselItem key={article.id} className="basis-full pl-0" data-testid={`${dataTestId}-slide-${index}`}>
                  <Link
                    href={article.seoInfo.canonicalUrl}
                    simplified
                    className="relative block w-full overflow-hidden rounded"
                    aria-label={article.title}
                    data-testid={`${dataTestId}-slide-${index}-image-link`}
                  >
                    <Image
                      src={imagePathResolver(article.featuredImage?.filePath ?? '', baseImgUrl)}
                      alt={article.featuredImage?.altText ?? ''}
                      width={520}
                      height={400}
                      sizes="(max-width: 768px) 100vw, 856px"
                      className="aspect-[520/400] w-full object-cover"
                      priority={index === 0}
                      data-testid={`${dataTestId}-slide-${index}-image`}
                    />
                  </Link>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious
              aria-label={t('previous')}
              className="left-0 top-1/2 h-20 w-10 -translate-y-1/2 rounded-none rounded-r-xs border-0 px-0 shadow-lg"
              data-testid={`${dataTestId}-previous`}
              data-track-name="Previous"
            />
            <CarouselNext
              aria-label={t('next')}
              className="right-0 top-1/2 h-20 w-10 -translate-y-1/2 rounded-none rounded-l-xs border-0 px-0 shadow-lg"
              data-testid={`${dataTestId}-next`}
              data-track-name="Next"
            />
          </div>
        </Carousel>

        <MagazinePromotedArticleContent article={activeArticle} baseImgUrl={baseImgUrl} />
      </div>

      <CarouselDots
        className="mb-0"
        dotSize="large"
        count={articles.length}
        selectedIndex={selectedIndex}
        onSelect={index => api?.scrollTo(index)}
        data-testid={`${dataTestId}-dots`}
      />
    </section>
  );
}

export { MagazinePromotedCarousel, type MagazinePromotedCarouselProps };
